import { MultipartUploadError, PantheonApiError } from "@/client/media-upload";

/**
 * Why a video response failed, specific enough to count in the event logs
 * without opening Sentry.
 *
 * - `pantheon_rejected` — our own API refused a step of the upload.
 * - `s3_rejected` — S3 or CloudFront answered a part upload with an error.
 * - `network_lost` — the transfer died with no response at all.
 * - `mutation_failed` — the file landed and saving the turn failed after it.
 * - `app_error` — a failure in our own code, outside the transfer itself.
 * - `unknown` — something was thrown that is not an Error.
 */
export type UploadFailureReason =
  | "pantheon_rejected"
  | "s3_rejected"
  | "network_lost"
  | "mutation_failed"
  | "app_error"
  | "unknown";

export interface UploadFailure {
  reason: UploadFailureReason;
  /** The pantheon multipart endpoint that refused, when one did. */
  operation?: string;
  /** HTTP status from pantheon or S3. Absent when no response arrived. */
  status?: number;
  media_id?: string;
  upload_id?: string;
}

/**
 * Classify a failed response submission from the error alone.
 *
 * `uploadSucceeded` is checked first because once the file has landed, whatever
 * is thrown afterwards comes from saving the turn, not from the transfer.
 */
export function describeUploadFailure(
  error: unknown,
  { uploadSucceeded }: { uploadSucceeded: boolean }
): UploadFailure {
  if (uploadSucceeded) {
    return { reason: "mutation_failed" };
  }

  if (error instanceof PantheonApiError) {
    return {
      reason: "pantheon_rejected",
      operation: error.operation,
      status: error.status,
    };
  }

  if (error instanceof MultipartUploadError) {
    return {
      // No status means no response ever arrived, so the transfer died on the
      // wire rather than being refused.
      reason: error.status == null ? "network_lost" : "s3_rejected",
      status: error.status,
      media_id: error.mediaId,
      upload_id: error.uploadId,
    };
  }

  return { reason: error instanceof Error ? "app_error" : "unknown" };
}
